import React, { useEffect, useState } from "react";
import { Star, BadgeCheck, MapPin, Briefcase } from "lucide-react";
import api from "@/lib/api";
import AgentAvatar from "@/components/AgentAvatar";

/**
 * Compact trust signal card surfaced to clients during the marketplace claim
 * flow and on TaskDetail once an agent is assigned. Pulls the public profile
 * (rating + completed errands count) from GET /api/agents/:id/public.
 *
 * Variants:
 *   - "inline" (default) — single horizontal row, fits inside list cards
 *   - "panel" — a card on its own, for TaskDetail / claim modal
 */
export default function AgentTrustCard({ agentId, agentName, variant = "inline" }) {
  const [profile, setProfile] = useState(null);

  useEffect(() => {
    if (!agentId) {
      setProfile(null);
      return;
    }
    let active = true;
    (async () => {
      try {
        const { data } = await api.get(`/agents/${agentId}/public`);
        if (active) setProfile(data);
      } catch {
        if (active) setProfile(null);
      }
    })();
    return () => { active = false; };
  }, [agentId]);

  if (!agentId) return null;

  // Fall back gracefully while the public profile is loading — show the name
  // so the layout doesn't jump.
  const rating = profile?.rating ?? 0;
  const completed = profile?.completed_tasks ?? 0;
  const city = profile?.city_coverage;
  const verified = profile?.verification_status === "verified";
  const displayName = profile?.name || agentName || "Agent";

  if (variant === "inline") {
    return (
      <div
        data-testid="agent-trust-inline"
        data-agent-id={agentId}
        className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-[var(--eb-text-dim)]"
      >
        <AgentAvatar src={profile?.photo_url} name={displayName} size={28} />
        <span className="inline-flex items-center gap-1 font-[700] text-[var(--eb-text)]">
          {displayName}
          {verified && <BadgeCheck className="w-3.5 h-3.5 text-[var(--eb-primary)]" data-testid="agent-trust-verified" />}
        </span>
        <span className="inline-flex items-center gap-1" data-testid="agent-trust-rating">
          <Star className="w-3.5 h-3.5 fill-current text-amber-500" />
          {(rating || 0).toFixed(1)}
        </span>
        <span className="inline-flex items-center gap-1" data-testid="agent-trust-completed">
          <Briefcase className="w-3.5 h-3.5" /> {completed} errand{completed === 1 ? "" : "s"}
        </span>
        {city && (
          <span className="inline-flex items-center gap-1">
            <MapPin className="w-3.5 h-3.5" /> {city}
          </span>
        )}
      </div>
    );
  }

  // Panel variant
  return (
    <div
      data-testid="agent-trust-panel"
      data-agent-id={agentId}
      className="eb-card p-5"
    >
      <div className="flex items-start gap-4">
        <AgentAvatar src={profile?.photo_url} name={displayName} size={64} />
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--eb-text-dim)]">Your agent</div>
          <div className="mt-1 text-xl font-[800] inline-flex items-center gap-2">
            {displayName}
            {verified && <BadgeCheck className="w-4 h-4 text-[var(--eb-primary)]" data-testid="agent-trust-verified" />}
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-[var(--eb-text-dim)]">
            <span className="inline-flex items-center gap-1" data-testid="agent-trust-rating">
              <Star className="w-4 h-4 fill-current text-amber-500" />
              <span className="font-[700] text-[var(--eb-text)]">{(rating || 0).toFixed(1)}</span>
              {rating > 0 && <span className="text-[var(--eb-text-faint)]">/ 5</span>}
            </span>
            <span className="inline-flex items-center gap-1" data-testid="agent-trust-completed">
              <Briefcase className="w-4 h-4" />
              <span className="font-[700] text-[var(--eb-text)]">{completed}</span>
              errand{completed === 1 ? "" : "s"} completed
            </span>
            {city && (
              <span className="inline-flex items-center gap-1">
                <MapPin className="w-4 h-4" /> {city}
              </span>
            )}
          </div>
          {profile?.bio && (
            <p className="mt-3 text-sm text-[var(--eb-text-dim)] italic">&quot;{profile.bio}&quot;</p>
          )}
        </div>
      </div>
    </div>
  );
}
